import { listingAuthoringValues, type ListingAuthoringRequest, type ListingAuthoringResult } from "./listingAuthoring";
import type { ResearchBatchInput, ResearchBatchOutcome } from "./researchReview";
import { usePhotoImportRefresh } from "../hooks/use-photo-import-refresh";
import { usePhotoReadRefresh } from "../hooks/use-photo-read-refresh";
import { measureTask } from "../lib/diagnostics";
import { savePreparedListing } from "./preparedListing";
import type { CatalogueUpdateInput } from "./catalogue";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { syncNotifications } from "./notifications";
import { photoProgressTracker, type PhotoProgressCallback } from "./photoProgress";
import type { ManualListingInput, ManualListingResult } from "./manualListings";
import {
  createReOSRepository,
  accountWorkspaceSummariesForDatabase,
  type AccountDataExport,
  type AccountPasskey,
  type AccountWorkspaceSummary,
  type AccountDeletionResult,
  type AccountSession,
  type EmailVerificationRequestResult,
  type EmailSignInCodeRequestResult,
  type EbayIntegrationConfiguration,
  type EbayImportPreview,
  type EbayImportResult,
  type EbayIntegrationStatus,
  type EtsyIntegrationConfiguration,
  type EtsyIntegrationStatus,
  type EtsyTaxonomyChoice,
  type LocalBackupImportResult,
  type ListingPublication,
  type MarketplaceLifecycleOperation,
  type MarketplaceLifecycleResult,
  type ItemResearchRunRequest,
  type PasskeyOptionsResult,
  type ReOSRepositoryMode,
  type WorkspaceInvitationSummary,
  type WorkspaceMemberSummary,
  type WorkspaceCollectionPage,
  isExpiredPhotoReplacementError,
  workspaceMemberSummariesForDatabase
} from "./repository";
import type { WorkspaceCollectionQuery, WorkspaceCollectionResource } from "./workspaceCollections";
import { hasEntitlement as hasWorkspaceEntitlement, limitForEntitlement, subscriptionForWorkspace } from "./entitlements";
import type { SaleFulfilmentUpdate, SaleInput, WorkspaceSettingsUpdate } from "./workflows";
import {
  activeWorkspace,
  agingStockItems,
  analyticsSummary,
  currentUser,
  dashboardSummary,
  generateSKU,
  itemFor,
  listingsFor,
  photosFor,
  platformPerformance,
  previewListingDrafts,
  primaryPhoto,
  profitOverTime,
  stockAgeBuckets,
  workspaceAccounts,
  workspaceCategories,
  workspaceItems,
  workspaceListings,
  workspaceNotifications,
  workspaceSales
} from "./logic";
import { demoDatabase, emptyDatabase } from "./seed";
import type {
  InventoryItem,
  InventoryItemInput,
  InventoryItemUpdate,
  ItemPhoto,
  EntitlementKey,
  ListingDraftUpdate,
  Marketplace,
  MarketplaceAccount,
  MarketplaceImportApplyResult,
  MarketplaceImportDecisionInput,
  MarketplaceImportPage,
  MarketplaceImportRecord,
  MarketplaceImportRun,
  MarketplaceImportStatus,
  MarketplaceListing,
  PhotoInput,
  ReOSDatabase,
  ReOSNotification,
  Sale,
  SaleStatus,
  SubscriptionPlan,
  WorkspaceSubscription,
  User,
  Workspace
} from "./types";

interface ReOSContextValue {
  database: ReOSDatabase;
  user?: User;
  workspace?: Workspace;
  subscription?: WorkspaceSubscription;
  isAuthenticated: boolean;
  isLoading: boolean;
  persistenceMode: ReOSRepositoryMode;
  persistenceLabel: string;
  persistenceError: string | null;
  inventoryItems: InventoryItem[];
  listings: MarketplaceListing[];
  sales: Sale[];
  notifications: ReOSNotification[];
  sessions: AccountSession[];
  passkeys: AccountPasskey[];
  accountWorkspaces: AccountWorkspaceSummary[];
  workspaceMembers: WorkspaceMemberSummary[];
  workspaceInvitations: WorkspaceInvitationSummary[];
  workspaceMemberLimit: number;
  marketplaceAccounts: MarketplaceAccount[];
  ebayIntegration?: EbayIntegrationStatus;
  etsyIntegration?: EtsyIntegrationStatus;
  categories: ReturnType<typeof workspaceCategories>;
  listWorkspaceCollection: (resource: WorkspaceCollectionResource, query: WorkspaceCollectionQuery) => Promise<WorkspaceCollectionPage>;
  loadDemoData: () => void;
  createPasskeyRegistrationOptions: (input: { email: string; name: string; workspaceName?: string; invitationToken?: string; turnstileToken?: string }) => Promise<PasskeyOptionsResult>;
  completePasskeyRegistration: (challengeID: string, response: unknown) => Promise<void>;
  createPasskeyAuthenticationOptions: (email?: string, turnstileToken?: string) => Promise<PasskeyOptionsResult>;
  completePasskeyAuthentication: (challengeID: string, response: unknown) => Promise<void>;
  requestEmailSignInCode: (input: {
    email: string;
    name?: string;
    workspaceName?: string;
    invitationToken?: string;
    intent: "create" | "signin";
    turnstileToken?: string;
  }) => Promise<EmailSignInCodeRequestResult>;
  pendingDeviceSignInSetup: boolean;
  completeEmailSignInCode: (
    email: string,
    code: string,
    options?: { offerDeviceSignIn?: boolean },
  ) => Promise<void>;
  finishDeviceSignInSetup: () => void;
  signOut: () => Promise<void>;
  refreshSessions: () => Promise<void>;
  revokeSession: (sessionID: string) => Promise<void>;
  refreshPasskeys: () => Promise<void>;
  createAccountPasskeyRegistrationOptions: () => Promise<PasskeyOptionsResult>;
  completeAccountPasskeyRegistration: (challengeID: string, response: unknown) => Promise<void>;
  revokePasskey: (passkeyID: string) => Promise<void>;
  refreshAccountWorkspaces: () => Promise<void>;
  refreshWorkspace: () => Promise<void>;
  switchWorkspace: (workspaceID: string) => Promise<void>;
  updateWorkspaceSettings: (update: WorkspaceSettingsUpdate) => Promise<void>;
  refreshWorkspaceMembers: () => Promise<void>;
  addWorkspaceMember: (email: string, role: "admin" | "member") => Promise<void>;
  removeWorkspaceMember: (memberID: string) => Promise<void>;
  refreshWorkspaceInvitations: () => Promise<void>;
  createWorkspaceInvitation: (email: string, role: "admin" | "member") => Promise<void>;
  revokeWorkspaceInvitation: (invitationID: string) => Promise<void>;
  acceptWorkspaceInvitation: (token: string) => Promise<void>;
  requestEmailVerification: () => Promise<EmailVerificationRequestResult>;
  completeEmailVerification: (token: string) => Promise<void>;
  exportAccountData: () => Promise<AccountDataExport>;
  accountArchiveDownloadURL: () => string;
  importLocalBackup: (backup: unknown) => Promise<LocalBackupImportResult>;
  deleteAccount: (confirmation: string) => Promise<AccountDeletionResult>;
  startBillingCheckout: (plan: SubscriptionPlan) => Promise<string>;
  startAssistantRequestPackCheckout: () => Promise<string>;
  getAssistantUsage: () => Promise<{ used: number; included: number; packRemaining: number; packPurchased: number; limit: number; remaining: number; resetsAt: string; breakdown: { conversations: number; automations: number; standard: number; advanced: number } }>;
  openBillingPortal: () => Promise<string>;
  refreshEbayIntegration: () => Promise<void>;
  getEbayImportPreview: (page?: number, pageSize?: number) => Promise<EbayImportPreview>;
  importEbayListings: (input: { externalListingIDs: string[]; page: number; pageSize: number }) => Promise<EbayImportResult>;
  notificationSettingsRequest: <T>(method: "GET" | "POST" | "DELETE", action?: string, body?: unknown) => Promise<T>;
  spreadsheetRequest: <T>(input: import("./spreadsheetMigration").MigrationRequest) => Promise<T>;
  channelRequest: <T>(accountID: string, action: import("./repository").ChannelRequestAction, body?: unknown, query?: Record<string, string>) => Promise<T>;
  getMarketplaceImportPreview: (marketplace: Extract<Marketplace, "ebay" | "etsy">, page?: number, pageSize?: number, runID?: string) => Promise<MarketplaceImportPage>;
  getMarketplaceImportStatus: (marketplace: Extract<Marketplace, "ebay" | "etsy">, runID: string, recordIDs: string[]) => Promise<MarketplaceImportStatus>;
  saveMarketplaceImportDecisions: (marketplace: Extract<Marketplace, "ebay" | "etsy">, runID: string, decisions: MarketplaceImportDecisionInput[]) => Promise<{ run: MarketplaceImportRun; records: MarketplaceImportRecord[] }>;
  applyMarketplaceImport: (marketplace: Extract<Marketplace, "ebay" | "etsy">, runID: string, recordIDs?: string[], expectedRevisions?: Record<string, number>, openingQuantities?: Record<string, number>) => Promise<MarketplaceImportApplyResult>;
  startEbayConnection: (options?: { accessMode?: "read_only" | "read_write"; returnPath?: string; financialAccess?: boolean }) => Promise<string>;
  updateEbayIntegration: (input: EbayIntegrationConfiguration) => Promise<void>;
  disconnectEbay: () => Promise<void>;
  refreshEtsyIntegration: () => Promise<void>;
  startEtsyConnection: () => Promise<string>;
  updateEtsyIntegration: (input: EtsyIntegrationConfiguration) => Promise<void>;
  disconnectEtsy: () => Promise<void>;
  searchEtsyTaxonomy: (query: string) => Promise<EtsyTaxonomyChoice[]>;
  refreshMarketplaceIntegrations: () => Promise<void>;
  runMarketplaceLifecycle: (
    marketplace: Marketplace,
    operation: MarketplaceLifecycleOperation,
    payload?: Record<string, unknown>,
    resourceID?: string
  ) => Promise<MarketplaceLifecycleResult>;
  reconcileMarketplaceLifecycle: (
    marketplace: Marketplace,
    operation: MarketplaceLifecycleOperation,
    idempotencyKey: string
  ) => Promise<MarketplaceLifecycleResult>;
  resetAllData: () => void;
  markNotificationRead: (notificationID: string) => Promise<void>;
  markNotificationsRead: (notificationIDs: string[]) => Promise<void>;
  createInventoryWorkflow: (input: InventoryItemInput, photos: PhotoInput[], selectedMarketplaces: Marketplace[], onProgress?: PhotoProgressCallback) => Promise<InventoryItem>;
  updateCatalogue: (input: CatalogueUpdateInput) => Promise<{ items: InventoryItem[]; replayed: boolean }>;
  updateInventoryItem: (itemID: string, update: InventoryItemUpdate) => Promise<InventoryItem>;
  startResearchBatch: (input: ResearchBatchInput) => Promise<ResearchBatchOutcome[]>;
  startItemResearch: (itemID: string, input: ItemResearchRunRequest) => Promise<InventoryItem>;
  refreshItemResearch: (itemID: string) => Promise<InventoryItem>;
  createListingDrafts: (itemID: string, selectedMarketplaces: Marketplace[]) => Promise<MarketplaceListing[]>;
  savePreparedListing: (itemID: string, marketplace: Marketplace, update: ListingDraftUpdate, listingID?: string) => Promise<MarketplaceListing>;
  addItemPhoto: (itemID: string, photo: PhotoInput) => Promise<ItemPhoto>;
  addItemPhotos: (itemID: string, photos: PhotoInput[], onProgress?: PhotoProgressCallback) => Promise<ItemPhoto[]>;
  updateItemPhoto: (photoID: string, photo: PhotoInput, onProgress?: PhotoProgressCallback) => Promise<ItemPhoto>;
  removeItemPhoto: (photoID: string) => Promise<void>;
  previewListingDrafts: typeof previewListingDrafts;
  generateSKU: (title: string, size?: string) => string;
  itemFor: (itemID: string | undefined) => InventoryItem | undefined;
  photosFor: (itemID: string | undefined) => ItemPhoto[];
  primaryPhoto: (itemID: string | undefined) => ItemPhoto | undefined;
  listingsFor: (itemID: string | undefined) => MarketplaceListing[];
  dashboardSummary: typeof dashboardSummary;
  analyticsSummary: typeof analyticsSummary;
  profitOverTime: typeof profitOverTime;
  stockAgeBuckets: typeof stockAgeBuckets;
  platformPerformance: typeof platformPerformance;
  agingStockItems: typeof agingStockItems;
  authorListing: (listingID: string, request: ListingAuthoringRequest) => Promise<ListingAuthoringResult>;
  updateListingDraft: (listingID: string, update: ListingDraftUpdate) => Promise<MarketplaceListing>;
  updateListingURL: (listingID: string, externalURL: string) => Promise<void>;
  publishListing: (listingID: string) => Promise<ListingPublication>;
  markListingLive: (listingID: string, externalURL?: string) => Promise<void>;
  markItemsListedManually: (input: ManualListingInput) => Promise<ManualListingResult>;
  endListing: (listingID: string) => Promise<void>;
  archiveInventoryItem: (itemID: string) => Promise<void>;
  recordSale: (input: SaleInput) => Promise<Sale>;
  updateSaleStatus: (saleID: string, status: SaleStatus) => Promise<void>;
  updateSaleFulfilment: (saleID: string, update: SaleFulfilmentUpdate) => Promise<void>;
  bulkPackSales: (saleIDs: string[]) => Promise<{ updatedSaleIDs: string[]; skipped: Array<{ saleID: string; reason: string }> }>;
  hasEntitlement: (key: EntitlementKey) => boolean;
}

const ReOSContext = createContext<ReOSContextValue | undefined>(undefined);

export async function retryExpiredPhotoReplacement<T>(
  preparedPhoto: PhotoInput,
  prepareFresh: () => Promise<PhotoInput>,
  persist: (photo: PhotoInput) => Promise<T>
) {
  try {
    return await persist(preparedPhoto);
  } catch (error) {
    if (!isExpiredPhotoReplacementError(error)) {
      throw error;
    }
    return persist(await prepareFresh());
  }
}

export function ReOSProvider({ children }: { children: ReactNode }) {
  const repository = useMemo(() => createReOSRepository(), []);
  const [database, setDatabase] = useState<ReOSDatabase>(() => emptyDatabase());
  usePhotoImportRefresh(database, setDatabase, async (workspaceID) => hydrateRepositoryDatabase(await repository.loadWorkspace(workspaceID, database), repository.mode), repository.mode === "api");
  usePhotoReadRefresh(database, setDatabase, repository.refreshPhotoReadURLs?.bind(repository));
  const [sessions, setSessions] = useState<AccountSession[]>([]);
  const [passkeys, setPasskeys] = useState<AccountPasskey[]>([]);
  const [pendingDeviceSignInSetup, setPendingDeviceSignInSetup] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem("listwork:pending-device-sign-in-setup") === "true";
    } catch {
      return false;
    }
  });
  const [accountWorkspaces, setAccountWorkspaces] = useState<AccountWorkspaceSummary[]>([]);
  const [workspaceMemberSummaries, setWorkspaceMemberSummaries] = useState<WorkspaceMemberSummary[]>([]);
  const [workspaceInvitationSummaries, setWorkspaceInvitationSummaries] = useState<WorkspaceInvitationSummary[]>([]);
  const [ebayIntegration, setEbayIntegration] = useState<EbayIntegrationStatus>();
  const [etsyIntegration, setEtsyIntegration] = useState<EtsyIntegrationStatus>();
  const [isLoading, setIsLoading] = useState(true);
  const [persistenceError, setPersistenceError] = useState<string | null>(null);
  const accountWorkspaceLoadVersion = useRef(0);
  const pendingPhotoReplacements = useRef(
    new Map<string, { source: PhotoInput; prepared: PhotoInput }>()
  );

  useEffect(() => {
    pendingPhotoReplacements.current.clear();
  }, [database.activeWorkspaceID]);

  const loadAccountWorkspacesForDatabase = useCallback(
    async (sourceDatabase: ReOSDatabase) => {
      const loadVersion = ++accountWorkspaceLoadVersion.current;
      const fallback = accountWorkspaceSummariesForDatabase(sourceDatabase);
      setAccountWorkspaces(fallback);
      if (repository.mode !== "api" || !sourceDatabase.currentUserID) {
        return fallback;
      }

      const loaded = await repository.listAccountWorkspaces(sourceDatabase);
      const summaries = loaded.length > 0 ? loaded : fallback;
      if (loadVersion === accountWorkspaceLoadVersion.current) {
        setAccountWorkspaces(summaries);
      }
      return summaries;
    },
    [repository]
  );

  const refreshAccountWorkspacesInBackground = useCallback(
    (sourceDatabase: ReOSDatabase) => {
      const refresh = loadAccountWorkspacesForDatabase(sourceDatabase);
      const loadVersion = accountWorkspaceLoadVersion.current;
      refresh.catch((error: unknown) => {
        if (loadVersion === accountWorkspaceLoadVersion.current) {
          setPersistenceError(error instanceof Error ? error.message : "Unable to refresh account workspaces.");
        }
      });
    },
    [loadAccountWorkspacesForDatabase]
  );

  useEffect(() => {
    let mounted = true;
    repository
      .loadDatabase()
      .then(async (loaded) => {
        if (!mounted) {
          return;
        }
        const hydrated = hydrateRepositoryDatabase(loaded, repository.mode);
        await loadAccountWorkspacesForDatabase(hydrated);
        if (!mounted) {
          return;
        }
        setDatabase(hydrated);
        setPersistenceError(null);
      })
      .catch((error: unknown) => {
        if (!mounted) {
          return;
        }
        setDatabase(emptyDatabase());
        setAccountWorkspaces([]);
        setPersistenceError(error instanceof Error ? error.message : "Unable to load workspace data.");
      })
      .finally(() => {
        if (mounted) {
          setIsLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [loadAccountWorkspacesForDatabase, repository]);

  const persistDatabase = useCallback(
    async (nextDatabase: ReOSDatabase) => {
      const synced = syncNotifications(nextDatabase);
      setDatabase(synced);
      setPersistenceError(null);
      try {
        await repository.saveDatabase(synced);
      } catch (error: unknown) {
        setPersistenceError(error instanceof Error ? error.message : "Unable to save workspace data.");
        throw error;
      }
    },
    [repository]
  );

  const commitDatabase = useCallback(
    (nextDatabase: ReOSDatabase) => {
      persistDatabase(nextDatabase).catch(() => undefined);
    },
    [persistDatabase]
  );

  const applyRepositoryDatabase = useCallback(
    async (nextDatabase: ReOSDatabase) => {
      if (repository.mode === "api") {
        setDatabase(hydrateRepositoryDatabase(nextDatabase, repository.mode));
        setPersistenceError(null);
      } else {
        await persistDatabase(nextDatabase);
      }
    },
    [persistDatabase, repository.mode]
  );

  const applyResearchDatabase = useCallback((next: ReOSDatabase) => {
    // Research polling may finish after an inventory edit. Never replace newer item data.
    setDatabase((current) => {
      if (current.activeWorkspaceID !== next.activeWorkspaceID) return current;
      const incoming = new Map(next.inventoryItems.map((item) => [item.id, item]));
      return { ...current, inventoryItems: current.inventoryItems.map((item) => {
        const updated = incoming.get(item.id);
        return updated && updated.updatedAt > item.updatedAt ? updated : item;
      }) };
    });
  }, []);

  const cancelPreparedPhotos = useCallback(
    async (workspaceID: string, photos: PhotoInput[]) => {
      await Promise.allSettled(
        photos.map((photo) => repository.cancelPreparedPhoto(workspaceID, photo))
      );
    },
    [repository]
  );

  const preparePhotosForSave = useCallback(
    async (workspaceID: string, photos: PhotoInput[], startIndex: number, onProgress?: PhotoProgressCallback) => {
      const report = photoProgressTracker(photos.map((photo, index) => photo.fileName ?? `Photo ${index + 1}`), onProgress);
      const results = await Promise.allSettled(
        photos.map((photo, index) =>
          repository.preparePhotoForSave(workspaceID, photo, startIndex + index, { onProgress: (stage) => report(index, stage) })
        )
      );
      const preparedPhotos = results.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : []
      );
      const rejected = results.find(
        (result): result is PromiseRejectedResult => result.status === "rejected"
      );
      if (rejected) {
        await cancelPreparedPhotos(workspaceID, preparedPhotos);
        throw rejected.reason;
      }
      return preparedPhotos;
    },
    [cancelPreparedPhotos, repository]
  );

  const resetDatabase = useCallback(() => {
    const empty = emptyDatabase();
    accountWorkspaceLoadVersion.current += 1;
    setDatabase(empty);
    setPasskeys([]);
    setAccountWorkspaces([]);
    setPersistenceError(null);
    repository.clearDatabase().catch((error: unknown) => {
      setPersistenceError(error instanceof Error ? error.message : "Unable to reset workspace data.");
    });
  }, [repository]);

  const loadDemoDatabase = useCallback(() => {
    if (repository.mode !== "local_demo") {
      setPersistenceError(
        repository.mode === "api"
          ? "Demo data is unavailable in this workspace."
          : "Local demo data is unavailable in this build."
      );
      return;
    }
    commitDatabase(demoDatabase());
  }, [commitDatabase, repository.mode]);

  const refreshSessions = useCallback(async () => {
    if (repository.mode !== "api") {
      setSessions([]);
      return;
    }
    try {
      const loaded = await repository.listSessions();
      setSessions(loaded);
      setPersistenceError(null);
    } catch (error: unknown) {
      setPersistenceError(error instanceof Error ? error.message : "Unable to load account sessions.");
    }
  }, [repository]);

  const refreshPasskeys = useCallback(async () => {
    if (repository.mode !== "api") {
      setPasskeys([]);
      return;
    }
    try {
      const loaded = await repository.listPasskeys();
      setPasskeys(loaded);
      setPersistenceError(null);
    } catch (error: unknown) {
      setPersistenceError(error instanceof Error ? error.message : "Unable to load account passkeys.");
    }
  }, [repository]);

  const refreshAccountWorkspaces = useCallback(async () => {
    try {
      await loadAccountWorkspacesForDatabase(database);
      setPersistenceError(null);
    } catch (error: unknown) {
      setPersistenceError(error instanceof Error ? error.message : "Unable to load workspaces.");
    }
  }, [database, loadAccountWorkspacesForDatabase]);

  const refreshWorkspace = useCallback(async () => {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      return;
    }
    const loaded = hydrateRepositoryDatabase(await repository.loadWorkspace(workspaceID), repository.mode);
    setDatabase(loaded);
    await loadAccountWorkspacesForDatabase(loaded);
    setPersistenceError(null);
  }, [database.activeWorkspaceID, loadAccountWorkspacesForDatabase, repository]);

  const switchWorkspace = useCallback(
    async (workspaceID: string) => {
      assertMarketplaceImportIdle();
      const targetWorkspaceID = workspaceID.trim();
      if (!targetWorkspaceID || targetWorkspaceID === database.activeWorkspaceID) {
        return;
      }
      if (accountWorkspaces.length > 0 && !accountWorkspaces.some((workspace) => workspace.id === targetWorkspaceID)) {
        throw new Error("Workspace is not available for this account.");
      }
      const loaded = hydrateRepositoryDatabase(await repository.loadWorkspace(targetWorkspaceID), repository.mode);
      setWorkspaceMemberSummaries([]);
      setWorkspaceInvitationSummaries([]);
      setDatabase(loaded);
      await loadAccountWorkspacesForDatabase(loaded);
      setPersistenceError(null);
    },
    [accountWorkspaces, database.activeWorkspaceID, loadAccountWorkspacesForDatabase, repository]
  );

  const refreshWorkspaceMembers = useCallback(async () => {
    if (repository.mode !== "api" || !database.activeWorkspaceID) {
      const localMembers = await repository.listWorkspaceMembers(database).catch(() => ({ members: [], limit: 0 }));
      setWorkspaceMemberSummaries(localMembers.members);
      return;
    }
    try {
      const loaded = await repository.listWorkspaceMembers(database);
      setWorkspaceMemberSummaries(loaded.members);
      setPersistenceError(null);
    } catch (error: unknown) {
      setPersistenceError(error instanceof Error ? error.message : "Unable to load workspace members.");
    }
  }, [database, repository]);

  const refreshWorkspaceInvitations = useCallback(async () => {
    if (repository.mode !== "api" || !database.activeWorkspaceID) {
      setWorkspaceInvitationSummaries([]);
      return;
    }
    try {
      const loaded = await repository.listWorkspaceInvitations(database);
      setWorkspaceInvitationSummaries(loaded.invitations);
      setPersistenceError(null);
    } catch (error: unknown) {
      setPersistenceError(error instanceof Error ? error.message : "Unable to load team invitations.");
    }
  }, [database, repository]);

  const refreshEbayIntegration = useCallback(async () => {
    if (repository.mode !== "api" || !database.activeWorkspaceID) {
      setEbayIntegration(undefined);
      return;
    }
    const status = await repository.getEbayIntegration(database);
    setEbayIntegration(status);
  }, [database, repository]);

  const refreshEtsyIntegration = useCallback(async () => {
    if (repository.mode !== "api" || !database.activeWorkspaceID) {
      setEtsyIntegration(undefined);
      return;
    }
    setEtsyIntegration(await repository.getEtsyIntegration(database));
  }, [database, repository]);

  useEffect(() => {
    if (repository.mode !== "api" || !database.currentUserID || !database.activeWorkspaceID) {
      setEbayIntegration(undefined);
      return;
    }
    let mounted = true;
    repository.getEbayIntegration(database)
      .then((status) => {
        if (mounted) setEbayIntegration(status);
      })
      .catch(() => {
        if (mounted) setEbayIntegration(undefined);
      });
    return () => {
      mounted = false;
    };
  }, [database.activeWorkspaceID, database.currentUserID, repository]);

  useEffect(() => {
    if (repository.mode !== "api" || !database.currentUserID || !database.activeWorkspaceID) {
      setEtsyIntegration(undefined);
      return;
    }
    let mounted = true;
    repository.getEtsyIntegration(database)
      .then((status) => { if (mounted) setEtsyIntegration(status); })
      .catch(() => { if (mounted) setEtsyIntegration(undefined); });
    return () => { mounted = false; };
  }, [database.activeWorkspaceID, database.currentUserID, repository]);

  const revokeSession = useCallback(
    async (sessionID: string) => {
      const result = await repository.revokeSession(sessionID);
      if (result.current) {
        setSessions([]);
        setPasskeys([]);
        setAccountWorkspaces([]);
        setDatabase((current) => ({ ...current, currentUserID: undefined, activeWorkspaceID: undefined }));
        return;
      }
      await refreshSessions();
    },
    [refreshSessions, repository]
  );

  useEffect(() => {
    if (!database.currentUserID) {
      setSessions([]);
      setPasskeys([]);
      setWorkspaceMemberSummaries([]);
      setWorkspaceInvitationSummaries([]);
      return;
    }
    if (repository.mode !== "api") {
      setSessions([]);
      setPasskeys([]);
    }
    setWorkspaceMemberSummaries(workspaceMemberSummariesForDatabase(database));
    setWorkspaceInvitationSummaries([]);
  }, [database, repository.mode]);

  const value = useMemo<ReOSContextValue>(() => {
    const user = currentUser(database);
    const workspace = activeWorkspace(database);
    const subscription = subscriptionForWorkspace(database, workspace?.id);
    const inventoryItems = workspaceItems(database);
    const listings = workspaceListings(database);
    const sales = workspaceSales(database);
    const notifications = workspaceNotifications(database);
    const marketplaceAccounts = workspaceAccounts(database);
    const categories = workspaceCategories(database);

    return {
      database,
      user,
      workspace,
      subscription,
      isAuthenticated: Boolean(user && workspace),
      isLoading,
      persistenceMode: repository.mode,
      persistenceLabel: repository.label,
      persistenceError,
      inventoryItems,
      listings,
      sales,
      notifications,
      sessions,
      passkeys,
      pendingDeviceSignInSetup,
      accountWorkspaces,
      workspaceMembers: workspaceMemberSummaries,
      workspaceInvitations: workspaceInvitationSummaries,
      workspaceMemberLimit: limitForEntitlement(database, workspace?.id, "workspace_members"),
      marketplaceAccounts,
      ebayIntegration,
      etsyIntegration,
      categories,
      listWorkspaceCollection: (resource, query) => repository.listWorkspaceCollection(database, resource, query),
      loadDemoData: loadDemoDatabase,
      createPasskeyRegistrationOptions: async ({ email, name, workspaceName, invitationToken, turnstileToken }) => {
        if (!email.trim().includes("@")) {
          throw new Error("Enter a valid email address.");
        }
        if (!name.trim()) {
          throw new Error("Enter your name.");
        }
        return repository.createPasskeyRegistrationOptions({ email, name, workspaceName, invitationToken, turnstileToken });
      },
      completePasskeyRegistration: async (challengeID, response) => {
        const created = hydrateRepositoryDatabase(await repository.completePasskeyRegistration({ challengeID, response }), repository.mode);
        setDatabase(created);
        setPersistenceError(null);
        refreshAccountWorkspacesInBackground(created);
      },
      createPasskeyAuthenticationOptions: async (email, turnstileToken) => {
        return repository.createPasskeyAuthenticationOptions({ email: email?.trim() || undefined, turnstileToken });
      },
      completePasskeyAuthentication: async (challengeID, response) => {
        const loaded = hydrateRepositoryDatabase(await repository.completePasskeyAuthentication({ challengeID, response }), repository.mode);
        setDatabase(loaded);
        setPersistenceError(null);
        refreshAccountWorkspacesInBackground(loaded);
      },
      requestEmailSignInCode: async ({ email, name, workspaceName, invitationToken, intent, turnstileToken }) => {
        if (!email.trim().includes("@")) {
          throw new Error("Enter a valid email address.");
        }
        if (intent === "create" && !name?.trim()) {
          throw new Error("Enter your name.");
        }
        return repository.requestEmailSignInCode({ email, name, workspaceName, invitationToken, intent, turnstileToken });
      },
      completeEmailSignInCode: async (email, code, options) => {
        if (!email.trim().includes("@")) {
          throw new Error("Enter a valid email address.");
        }
        if (!/^\d{6}$/.test(code.replace(/\s+/g, ""))) {
          throw new Error("Enter the 6-digit code.");
        }
        const loaded = hydrateRepositoryDatabase(await repository.completeEmailSignInCode({ email, code }), repository.mode);
        const shouldOfferDeviceSignIn = options?.offerDeviceSignIn === true && repository.mode === "api";
        setPendingDeviceSignInSetup(shouldOfferDeviceSignIn);
        try {
          if (shouldOfferDeviceSignIn) {
            window.sessionStorage.setItem("listwork:pending-device-sign-in-setup", "true");
          } else {
            window.sessionStorage.removeItem("listwork:pending-device-sign-in-setup");
          }
        } catch {
          // Authentication must still complete when session storage is unavailable.
        }
        setDatabase(loaded);
        setPersistenceError(null);
        refreshAccountWorkspacesInBackground(loaded);
      },
      finishDeviceSignInSetup: () => {
        setPendingDeviceSignInSetup(false);
        try {
          window.sessionStorage.removeItem("listwork:pending-device-sign-in-setup");
        } catch {
          // The in-memory checkpoint is sufficient when session storage is unavailable.
        }
      },
      signOut: async () => {
        await repository.signOut();
        accountWorkspaceLoadVersion.current += 1;
        setSessions([]);
        setPasskeys([]);
        setAccountWorkspaces([]);
        setWorkspaceMemberSummaries([]);
        setWorkspaceInvitationSummaries([]);
        setDatabase((current) => ({ ...current, currentUserID: undefined, activeWorkspaceID: undefined }));
      },
      refreshSessions,
      revokeSession,
      refreshPasskeys,
      createAccountPasskeyRegistrationOptions: async () => {
        if (repository.mode !== "api") {
          throw new Error("Passkey management is only available for cloud workspaces.");
        }
        return repository.createAccountPasskeyRegistrationOptions();
      },
      completeAccountPasskeyRegistration: async (challengeID, response) => {
        if (!challengeID.trim()) {
          throw new Error("Passkey setup expired. Try again.");
        }
        await repository.completeAccountPasskeyRegistration({ challengeID, response });
        await refreshPasskeys();
        setPersistenceError(null);
      },
      revokePasskey: async (passkeyID) => {
        if (!passkeyID.trim()) {
          throw new Error("Passkey is not available.");
        }
        await repository.revokePasskey(passkeyID);
        await refreshPasskeys();
        setPersistenceError(null);
      },
      refreshAccountWorkspaces,
      refreshWorkspace,
      switchWorkspace,
      updateWorkspaceSettings: async (update) => {
        if (
          !Object.prototype.hasOwnProperty.call(update, "name") &&
          !Object.prototype.hasOwnProperty.call(update, "defaultCurrency") &&
          !Object.prototype.hasOwnProperty.call(update, "marketplaceSignalEmail") &&
          !Object.prototype.hasOwnProperty.call(update, "marketplaceSignalsEnabledAt") &&
          !Object.prototype.hasOwnProperty.call(update, "marketplaceProfiles")
        ) {
          throw new Error("Change a workspace setting before saving.");
        }
        const nextDatabase = await repository.updateWorkspaceSettings(database, update);
        await applyRepositoryDatabase(nextDatabase);
        await loadAccountWorkspacesForDatabase(nextDatabase);
      },
      refreshWorkspaceMembers,
      addWorkspaceMember: async (email, role) => {
        if (!email.trim().includes("@")) {
          throw new Error("Enter a valid teammate email address.");
        }
        const result = await repository.addWorkspaceMember(database, { email, role });
        setWorkspaceMemberSummaries(result.members);
        await applyRepositoryDatabase(result.database);
      },
      removeWorkspaceMember: async (memberID) => {
        const result = await repository.removeWorkspaceMember(database, memberID);
        setWorkspaceMemberSummaries(result.members);
        await applyRepositoryDatabase(result.database);
      },
      refreshWorkspaceInvitations,
      createWorkspaceInvitation: async (email, role) => {
        if (!email.trim().includes("@")) {
          throw new Error("Enter a valid teammate email address.");
        }
        const result = await repository.createWorkspaceInvitation(database, { email, role });
        setWorkspaceInvitationSummaries(result.invitations);
        setPersistenceError(null);
      },
      revokeWorkspaceInvitation: async (invitationID) => {
        const result = await repository.revokeWorkspaceInvitation(database, invitationID);
        setWorkspaceInvitationSummaries(result.invitations);
        setPersistenceError(null);
      },
      acceptWorkspaceInvitation: async (token) => {
        if (!token.trim()) {
          throw new Error("Invitation link is invalid or expired.");
        }
        const result = await repository.acceptWorkspaceInvitation(token);
        setWorkspaceMemberSummaries(result.members);
        setWorkspaceInvitationSummaries(result.invitations);
        await loadAccountWorkspacesForDatabase(result.database);
        await applyRepositoryDatabase(result.database);
      },
      requestEmailVerification: async () => {
        return repository.requestEmailVerification();
      },
      completeEmailVerification: async (token) => {
        if (!token.trim()) {
          throw new Error("Email verification link is invalid or expired.");
        }
        await repository.completeEmailVerification(token);
        const loaded = hydrateRepositoryDatabase(await repository.loadDatabase(), repository.mode);
        await loadAccountWorkspacesForDatabase(loaded);
        setDatabase(loaded);
        setPersistenceError(null);
      },
      exportAccountData: async () => {
        return repository.exportAccountData();
      },
      accountArchiveDownloadURL: () => repository.accountArchiveDownloadURL(),
      importLocalBackup: async (backup) => {
        const result = await repository.importLocalBackup(database, backup);
        await applyRepositoryDatabase(result.database);
        return result;
      },
      deleteAccount: async (confirmation) => {
        if (confirmation.trim() !== "DELETE") {
          throw new Error("Type DELETE to confirm account deletion.");
        }
        const result = await repository.deleteAccount({ confirmation });
        setSessions([]);
        setPasskeys([]);
        setAccountWorkspaces([]);
        setDatabase(emptyDatabase());
        setPersistenceError(null);
        return result;
      },
      startBillingCheckout: async (plan) => {
        if (repository.mode !== "api") {
          throw new Error("Billing is only available for cloud workspaces.");
        }
        const result = await repository.createBillingCheckout({
          plan,
          successURL: `${window.location.origin}${window.location.pathname}?billing=success`,
          cancelURL: `${window.location.origin}${window.location.pathname}?billing=cancelled`
        });
        if (!result.url) {
          throw new Error("Billing checkout is unavailable.");
        }
        return result.url;
      },
      startAssistantRequestPackCheckout: async () => {
        if (repository.mode !== "api") {
          throw new Error("Billing is only available for cloud workspaces.");
        }
        const result = await repository.createBillingCheckout({
          product: "assistant_request_pack_250",
          successURL: `${window.location.origin}${window.location.pathname}?billing=success`,
          cancelURL: `${window.location.origin}${window.location.pathname}?billing=cancelled`
        });
        if (!result.url) {
          throw new Error("Assistant request pack checkout is unavailable.");
        }
        return result.url;
      },
      getAssistantUsage: async () => repository.getAssistantUsage(database),
      openBillingPortal: async () => {
        if (repository.mode !== "api") {
          throw new Error("Billing is only available for cloud workspaces.");
        }
        const result = await repository.createBillingPortal({
          returnURL: window.location.href
        });
        if (!result.url) {
          throw new Error("Billing portal is unavailable.");
        }
        return result.url;
      },
      refreshEbayIntegration,
      getEbayImportPreview: async (page = 1, pageSize = 50) => {
        if (repository.mode !== "api") {
          throw new Error("eBay imports are only available for cloud workspaces.");
        }
        return repository.getEbayImportPreview(database, page, pageSize);
      },
      importEbayListings: async (input) => {
        if (repository.mode !== "api") {
          throw new Error("eBay imports are only available for cloud workspaces.");
        }
        return repository.importEbayListings(database, input);
      },
      notificationSettingsRequest: async <T,>(method: "GET" | "POST" | "DELETE", action?: string, body?: unknown) => {
        if (!repository.notificationSettingsRequest) throw new Error("Notification settings require a cloud workspace.");
        return repository.notificationSettingsRequest<T>(database, method, action, body);
      },
      spreadsheetRequest: async <T,>(input: import("./spreadsheetMigration").MigrationRequest) => {
        if (!repository.spreadsheetRequest) throw new Error("Spreadsheet import needs a cloud workspace.");
        return repository.spreadsheetRequest<T>(database, input);
      },
      channelRequest: async <T,>(accountID: string, action: import("./repository").ChannelRequestAction, body?: unknown, query?: Record<string, string>) => {
        if (!repository.channelRequest) throw new Error("Connected listing activity needs a cloud workspace.");
        const result = await repository.channelRequest<T>(database, accountID, action, body, query);
        if (["photos/confirm", "photos/retry", "listing/refresh", "listing/edit", "listing/discard", "listing/update", "listing/update/check", "publication/submit", "publication/check"].includes(action)) await refreshWorkspace();
        return result;
      },
      getMarketplaceImportPreview: async (marketplace, page = 1, pageSize = 50, runID) => {
        if (repository.mode !== "api") throw new Error("Marketplace imports are only available for cloud workspaces.");
        return repository.getMarketplaceImportPreview(database, marketplace, page, pageSize, runID);
      },
      getMarketplaceImportStatus: async (marketplace, runID, recordIDs) => {
        if (repository.mode !== "api") throw new Error("Marketplace imports are only available for cloud workspaces.");
        return repository.getMarketplaceImportStatus(database, marketplace, runID, recordIDs);
      },
      saveMarketplaceImportDecisions: async (marketplace, runID, decisions) => {
        if (repository.mode !== "api") throw new Error("Marketplace imports are only available for cloud workspaces.");
        return repository.saveMarketplaceImportDecisions(database, marketplace, runID, decisions);
      },
      applyMarketplaceImport: async (marketplace, runID, recordIDs, expectedRevisions, openingQuantities) => {
        if (repository.mode !== "api") throw new Error("Marketplace imports are only available for cloud workspaces.");
        const result = await repository.applyMarketplaceImport(database, marketplace, runID, recordIDs, expectedRevisions, openingQuantities);
        await refreshWorkspace();
        return result;
      },
      startEbayConnection: async (options) => {
        if (repository.mode !== "api") {
          throw new Error("eBay connections are only available for cloud workspaces.");
        }
        return repository.startEbayConnection(database, options?.returnPath ?? "/channels", options?.accessMode, options?.financialAccess);
      },
      updateEbayIntegration: async (input) => {
        const status = await repository.updateEbayIntegration(database, input);
        setEbayIntegration(status);
        setPersistenceError(null);
      },
      disconnectEbay: async () => {
        const status = await repository.disconnectEbay(database);
        setEbayIntegration(status);
        setPersistenceError(null);
      },
      refreshEtsyIntegration,
      startEtsyConnection: async () => {
        if (repository.mode !== "api") {
          throw new Error("Etsy connections are only available for cloud workspaces.");
        }
        return repository.startEtsyConnection(database, "/channels");
      },
      updateEtsyIntegration: async (input) => {
        const status = await repository.updateEtsyIntegration(database, input);
        setEtsyIntegration(status);
        setPersistenceError(null);
      },
      disconnectEtsy: async () => {
        const status = await repository.disconnectEtsy(database);
        setEtsyIntegration(status);
        setPersistenceError(null);
      },
      searchEtsyTaxonomy: async (query) => repository.searchEtsyTaxonomy(database, query),
      refreshMarketplaceIntegrations: async () => {
        const integrations = await repository.listMarketplaceIntegrations(database);
        setDatabase((current) => ({
          ...current,
          marketplaceAccounts: [
            ...current.marketplaceAccounts.filter(
              (account) => account.workspaceID !== current.activeWorkspaceID
            ),
            ...integrations
          ]
        }));
        setPersistenceError(null);
      },
      runMarketplaceLifecycle: async (marketplace, operation, payload, resourceID) => {
        const result = await repository.runMarketplaceLifecycle(
          database,
          marketplace,
          operation,
          undefined,
          payload,
          resourceID
        );
        if (result.integration) {
          setDatabase((current) => ({
            ...current,
            marketplaceAccounts: [
              ...current.marketplaceAccounts.filter(
                (account) => account.id !== result.integration?.id
              ),
              result.integration!
            ]
          }));
        }
        setPersistenceError(null);
        return result;
      },
      reconcileMarketplaceLifecycle: async (marketplace, operation, idempotencyKey) => {
        const result = await repository.reconcileMarketplaceLifecycle(
          database,
          marketplace,
          operation,
          idempotencyKey
        );
        if (result.integration) {
          setDatabase((current) => ({
            ...current,
            marketplaceAccounts: [
              ...current.marketplaceAccounts.filter(
                (account) => account.id !== result.integration?.id
              ),
              result.integration!
            ]
          }));
        }
        setPersistenceError(null);
        return result;
      },
      resetAllData: resetDatabase,
      markNotificationRead: async (notificationID) => {
        const nextDatabase = await repository.markNotificationRead(database, notificationID);
        await applyRepositoryDatabase(nextDatabase);
      },
      markNotificationsRead: async (notificationIDs) => {
        let nextDatabase = database;
        for (const notificationID of notificationIDs) {
          nextDatabase = await repository.markNotificationRead(nextDatabase, notificationID);
        }
        await applyRepositoryDatabase(nextDatabase);
      },
      createInventoryWorkflow: async (input, photos, selectedMarketplaces, onProgress) => {
        if (!workspace) {
          throw new Error("Create or load a workspace first.");
        }
        const preparedPhotos = photos.length ? await measureTask("photo_upload", () => preparePhotosForSave(workspace.id, photos, 0, onProgress)) : [];
        try {
          onProgress?.({ phase: "saving", photos: [] });
          const result = await repository.createInventoryWorkflow(database, input, preparedPhotos, selectedMarketplaces);
          await applyRepositoryDatabase(result.database);
          return result.item;
        } catch (error) {
          await cancelPreparedPhotos(workspace.id, preparedPhotos);
          if (repository.mode === "api" && preparedPhotos.length > 0) {
            onProgress?.({ phase: "reconciling", photos: [] });
            try {
              const refreshed = hydrateRepositoryDatabase(
                await repository.loadWorkspace(workspace.id),
                repository.mode
              );
              await applyRepositoryDatabase(refreshed);
              const preparedAssetIDs = new Set(
                preparedPhotos.flatMap((photo) => photo.assetID ? [photo.assetID] : [])
              );
              const attachedPhotos = refreshed.itemPhotos.filter(
                (photo) => photo.assetID && preparedAssetIDs.has(photo.assetID)
              );
              const attachedItemIDs = new Set(attachedPhotos.map((photo) => photo.itemID));
              if (
                preparedAssetIDs.size === preparedPhotos.length &&
                attachedPhotos.length === preparedPhotos.length &&
                attachedItemIDs.size === 1
              ) {
                const [itemID] = attachedItemIDs;
                const committedItem = refreshed.inventoryItems.find((item) => item.id === itemID);
                if (committedItem) {
                  return committedItem;
                }
              }
            } catch {
              // Preserve the original mutation failure when reconciliation is unavailable.
            }
          }
          throw error;
        }
      },
      updateCatalogue: async (input) => {
        const result = await measureTask("catalogue_update", () => repository.updateCatalogue(database, input));
        await applyRepositoryDatabase(result.database);
        return { items: result.items, replayed: result.replayed };
      },
      updateInventoryItem: async (itemID, update) => {
        const result = await repository.updateInventoryItem(database, itemID, update);
        await applyRepositoryDatabase(result.database);
        return result.item;
      },
      startResearchBatch: async (input) => {
        if (!repository.startResearchBatch) throw new Error("Research requires a connected workspace.");
        const result = await measureTask("research", () => repository.startResearchBatch!(database, input));
        applyResearchDatabase(result.database);
        return result.outcomes;
      },
      startItemResearch: async (itemID, input) => {
        const result = await measureTask("research", () => repository.startItemResearch(database, itemID, input));
        applyResearchDatabase(result.database);
        return result.item;
      },
      refreshItemResearch: async (itemID) => {
        const result = await repository.refreshItemResearch(database, itemID);
        applyResearchDatabase(result.database);
        return result.item;
      },
      createListingDrafts: async (itemID, selectedMarketplaces) => {
        const result = await repository.createListingDrafts(database, itemID, selectedMarketplaces);
        await applyRepositoryDatabase(result.database);
        return result.listings;
      },
      savePreparedListing: (itemID, marketplace, update, listingID) => savePreparedListing(repository, database, itemID, marketplace, update, listingID, applyRepositoryDatabase),
      addItemPhoto: async (itemID, photo) => {
        if (!workspace) {
          throw new Error("Create or load a workspace first.");
        }
        const [preparedPhoto] = await preparePhotosForSave(
          workspace.id,
          [photo],
          photosFor(database, itemID).length
        );
        try {
          const result = await repository.addItemPhotos(database, itemID, [preparedPhoto]);
          await applyRepositoryDatabase(result.database);
          const savedPhoto = result.photos[0];
          if (!savedPhoto) {
            throw new Error("The photo was not attached to this item.");
          }
          return savedPhoto;
        } catch (error) {
          await cancelPreparedPhotos(workspace.id, [preparedPhoto]);
          throw error;
        }
      },
      addItemPhotos: async (itemID, photos, onProgress) => {
        if (!workspace) {
          throw new Error("Create or load a workspace first.");
        }
        const existingPhotoCount = photosFor(database, itemID).length;
        const preparedPhotos = await preparePhotosForSave(
          workspace.id,
          photos,
          existingPhotoCount,
          onProgress
        );
        try {
          onProgress?.({ phase: "saving", photos: [] });
          const result = await repository.addItemPhotos(database, itemID, preparedPhotos);
          await applyRepositoryDatabase(result.database);
          return result.photos;
        } catch (error) {
          await cancelPreparedPhotos(workspace.id, preparedPhotos);
          if (repository.mode === "api") {
            onProgress?.({ phase: "reconciling", photos: [] });
            try {
              const refreshed = hydrateRepositoryDatabase(
                await repository.loadWorkspace(workspace.id),
                repository.mode
              );
              await applyRepositoryDatabase(refreshed);
              const savedByAssetID = new Map(
                refreshed.itemPhotos
                  .filter((candidate) => candidate.itemID === itemID && candidate.assetID)
                  .map((candidate) => [candidate.assetID, candidate])
              );
              const reconciledPhotos = preparedPhotos.flatMap((preparedPhoto) => {
                const assetID = preparedPhoto.assetID;
                const savedPhoto = assetID ? savedByAssetID.get(assetID) : undefined;
                return savedPhoto ? [savedPhoto] : [];
              });
              if (reconciledPhotos.length === preparedPhotos.length) {
                return reconciledPhotos;
              }
            } catch {
              // Preserve the original mutation failure when reconciliation is unavailable.
            }
          }
          throw error;
        }
      },
      updateItemPhoto: async (photoID, photo, onProgress) => {
        if (!workspace) {
          throw new Error("Create or load a workspace first.");
        }
        const existingPhoto = database.itemPhotos.find((candidate) => candidate.id === photoID);
        if (!existingPhoto) {
          throw new Error("Photo does not exist in this workspace.");
        }
        const pendingReplacement = pendingPhotoReplacements.current.get(photoID);
        const report = photoProgressTracker([photo.fileName ?? "Edited photo"], onProgress);
        const context = { replacesPhotoID: photoID, onProgress: (stage: import("./photoProgress").PhotoSaveStage) => report(0, stage) };
        let preparedPhoto =
          pendingReplacement && photoInputsMatch(pendingReplacement.source, photo)
            ? pendingReplacement.prepared
            : await repository.preparePhotoForSave(
                workspace.id,
                photo,
                existingPhoto.sortOrder,
                context
              );
        try {
          const result = await retryExpiredPhotoReplacement(
            preparedPhoto,
            async () =>
              repository.preparePhotoForSave(
                workspace.id,
                photo,
                existingPhoto.sortOrder,
                context
              ),
            async (candidate) => {
              preparedPhoto = candidate;
              onProgress?.({ phase: "saving", photos: [] });
              return repository.updateItemPhoto(database, photoID, candidate);
            }
          );
          pendingPhotoReplacements.current.delete(photoID);
          await applyRepositoryDatabase(result.database);
          return result.photo;
        } catch (error) {
          if (preparedPhoto.assetID && preparedPhoto.assetID !== existingPhoto.assetID) {
            pendingPhotoReplacements.current.set(photoID, {
              source: photo,
              prepared: preparedPhoto
            });
          }
          throw error;
        }
      },
      removeItemPhoto: async (photoID) => {
        const nextDatabase = await repository.removeItemPhoto(database, photoID);
        await applyRepositoryDatabase(nextDatabase);
      },
      previewListingDrafts,
      generateSKU: (title, size) => generateSKU(database, title, size),
      itemFor: (itemID) => itemFor(database, itemID),
      photosFor: (itemID) => photosFor(database, itemID),
      primaryPhoto: (itemID) => primaryPhoto(database, itemID),
      listingsFor: (itemID) => listingsFor(database, itemID),
      dashboardSummary,
      analyticsSummary,
      profitOverTime,
      stockAgeBuckets,
      platformPerformance,
      agingStockItems,
      authorListing: async (listingID, request) => {
        let result: ListingAuthoringResult;
        if (repository.authorListing) result = await repository.authorListing(database, listingID, request);
        else {
          let listing = database.marketplaceListings.find((entry) => entry.id === listingID);
          if (!listing) throw new Error("Draft not found.");
          if (request.action === "generate") throw new Error("Writing help is available when signed in to your workspace. You can still edit this demo draft.");
          const initial = () => ({ version: crypto.randomUUID(), baseUpdatedAt: listing!.updatedAt, evidenceFingerprint: "local", values: listingAuthoringValues(listing!), details: "", questions: [], state: "editing" as const, photoIDs: [], updatedAt: new Date().toISOString() });
          let draft = listing.authoring ?? initial();
          if (request.action === "discard") draft = initial();
          else if (request.action !== "open") draft = { ...draft, version: crypto.randomUUID(), values: request.values ?? draft.values, details: request.details ?? draft.details };
          if (request.action === "accept") listing = (await repository.updateListingDraft(database, listingID, draft.values)).listing;
          result = { draft, listing: { ...listing, authoring: draft }, available: false, sourceChanged: false };
        }
        if (repository.mode === "api") setDatabase((current) => ({ ...current, marketplaceListings: current.marketplaceListings.map((entry) => entry.id === listingID ? result.listing : entry) }));
        else await applyRepositoryDatabase({ ...database, marketplaceListings: database.marketplaceListings.map((entry) => entry.id === listingID ? result.listing : entry) });
        return result;
      },
      updateListingDraft: async (listingID, update) => {
        const result = await repository.updateListingDraft(database, listingID, update);
        await applyRepositoryDatabase(result.database);
        return result.listing;
      },
      updateListingURL: async (listingID, externalURL) => {
        const nextDatabase = await repository.updateListingURL(database, listingID, externalURL);
        await applyRepositoryDatabase(nextDatabase);
      },
      publishListing: async (listingID) => {
        const result = await repository.publishListing(database, listingID);
        await applyRepositoryDatabase(result.database);
        return result.publication;
      },
      markListingLive: async (listingID, externalURL) => {
        const nextDatabase = await repository.markListingLive(database, listingID, externalURL);
        await applyRepositoryDatabase(nextDatabase);
      },
      markItemsListedManually: async (input) => {
        const result = await repository.markItemsListedManually(database, input);
        await applyRepositoryDatabase(result.database);
        return result;
      },
      endListing: async (listingID) => {
        const nextDatabase = await repository.endListing(database, listingID);
        await applyRepositoryDatabase(nextDatabase);
      },
      archiveInventoryItem: async (itemID) => {
        const nextDatabase = await repository.archiveInventoryItem(database, itemID);
        await applyRepositoryDatabase(nextDatabase);
      },
      recordSale: async (input) => {
        const result = await repository.recordSale(database, input);
        await applyRepositoryDatabase(result.database);
        return result.sale;
      },
      updateSaleStatus: async (saleID, status) => {
        const nextDatabase = await repository.updateSaleStatus(database, saleID, status);
        await applyRepositoryDatabase(nextDatabase);
      },
      updateSaleFulfilment: async (saleID, update) => {
        const nextDatabase = await repository.updateSaleFulfilment(database, saleID, update);
        await applyRepositoryDatabase(nextDatabase);
      },
      bulkPackSales: async (saleIDs) => {
        const result = await repository.bulkPackSales(database, saleIDs);
        await applyRepositoryDatabase(result.database);
        return { updatedSaleIDs: result.updatedSaleIDs, skipped: result.skipped };
      },
      hasEntitlement: (key) => hasWorkspaceEntitlement(database, workspace?.id, key)
    };
  }, [
    applyRepositoryDatabase,
    applyResearchDatabase,
    cancelPreparedPhotos,
    commitDatabase,
    database,
    isLoading,
    accountWorkspaces,
    ebayIntegration,
    etsyIntegration,
    loadDemoDatabase,
    loadAccountWorkspacesForDatabase,
    passkeys,
    pendingDeviceSignInSetup,
    persistenceError,
    preparePhotosForSave,
    refreshAccountWorkspaces,
    refreshWorkspace,
    refreshPasskeys,
    refreshSessions,
    refreshAccountWorkspacesInBackground,
    refreshWorkspaceInvitations,
    refreshWorkspaceMembers,
    refreshEbayIntegration,
    refreshEtsyIntegration,
    repository,
    resetDatabase,
    revokeSession,
    sessions,
    switchWorkspace,
    workspaceInvitationSummaries,
    workspaceMemberSummaries
  ]);

  const stableValue = useCallback(() => value, [value]);

  return <ReOSContext.Provider value={stableValue()}>{children}</ReOSContext.Provider>;
}

export function useReOS() {
  const context = useContext(ReOSContext);
  if (!context) {
    throw new Error("useReOS must be used inside ReOSProvider.");
  }
  return context;
}

export function hydrateRepositoryDatabase(database: ReOSDatabase, mode: ReOSRepositoryMode): ReOSDatabase {
  return mode === "api" ? database : syncNotifications(database);
}

function photoInputsMatch(left: PhotoInput, right: PhotoInput) {
  return (
    left.assetID === right.assetID &&
    left.url === right.url &&
    left.thumbnailURL === right.thumbnailURL &&
    left.width === right.width &&
    left.height === right.height &&
    left.type === right.type &&
    left.fileName === right.fileName &&
    left.mimeType === right.mimeType &&
    left.byteSize === right.byteSize &&
    JSON.stringify(left.treatment) === JSON.stringify(right.treatment)
  );
}
import { assertMarketplaceImportIdle } from "../marketplace-operation-guard";
